import React, { useState } from 'react';
import { ErrorMessage, useFormikContext } from 'formik';
import PerspectiveSelector from '../../molecules/PerspectiveSelector/PerspectiveSelector';
import './PerspectiveAssignmentsPanel.scss';
import { SchemaPerspective } from 'types/schema';
import { Sphere } from 'types/global';

import { useTranslation } from 'localization';

interface PerspectiveAssignmentsPanelProps {
  sphere: Sphere;
  perspectives: SchemaPerspective[];
  perspectiveAssignments: PerspectiveAssignment[];
  isEditing: boolean;
  predefinedRequiredPerspectiveSystemNames?: string[];
}

export type PerspectiveAssignment = {
  mainSphereId: string;
  perspectiveProperties: {
    id: string;
    system_name: string;
    name: string;
  };
  assigned: {
    sphere: Sphere;
    edge: {
      edgeId: string;
      urgency: number;
    };
  }[];
};

interface PerspectiveFormValues {
  properties: {
    [key: string]: string;
  };
  assignedPerspectives: any[];
}

const PerspectiveAssignmentsPanel: React.FC<
  PerspectiveAssignmentsPanelProps
> = ({
  sphere,
  perspectives,
  perspectiveAssignments,
  isEditing,
  predefinedRequiredPerspectiveSystemNames = [
    'center_center',
    'center_responsible',
    'center_parent',
  ],
}) => {
  const { t } = useTranslation('schema');
  const [showMore, setShowMore] = useState<boolean>(false);
  const { errors, values, setFieldValue } = useFormikContext<PerspectiveFormValues>();

  // Create updated sphere with current Formik assignedPerspectives
  const sphereWithCurrentAssignments: Sphere = {
    ...sphere,
    assignedPerspectives: values.assignedPerspectives,
  };

  // Get perspectives that have assignees (for view mode)
  const assignedPerspectives = perspectiveAssignments.filter(
    (pa) => pa.assigned.length > 0
  );

  // Predefined Required Perspectives
  const predefinedRequiredPerspectives =
    predefinedRequiredPerspectiveSystemNames
      .map((systemName) =>
        perspectives.find((persp) => persp.system_name === systemName)
      )
      .filter((persp): persp is SchemaPerspective => persp !== undefined);

  // Additional Required Perspectives
  const additionalRequiredPerspectives = perspectives
    .filter(
      (persp) =>
        persp.required &&
        !predefinedRequiredPerspectiveSystemNames.includes(persp.system_name)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  // Optional Perspectives
  const optionalPerspectives = perspectives
    .filter(
      (persp) =>
        !persp.required &&
        !predefinedRequiredPerspectiveSystemNames.includes(persp.system_name)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  // Handle Perspective Assignments
  const handleAssigneeSelected = (
    assignee: string | null,
    _sphereId: string,
    perspectiveSystemName: string,
    assigneeSphere?: Sphere
  ) => {
    // Use current Formik values instead of stale prop
    const currentAssignments = values.assignedPerspectives || [];

    if (assigneeSphere) {
      const existingAssignment = currentAssignments.find(
        (pa) => pa.perspectiveProperties.system_name === perspectiveSystemName
      );

      if (existingAssignment) {
        // Check if we're selecting the same assignee
        const currentAssigneeId = existingAssignment.assigned[0]?.sphere?.id;
        const newAssigneeId = assigneeSphere.id;

        // If it's the same assignee, don't update - preserve existing data
        if (currentAssigneeId === newAssigneeId) {
          return;
        }

        const updatedAssignments = currentAssignments.map((pa) =>
          pa.perspectiveProperties.system_name === perspectiveSystemName
            ? {
                ...pa,
                assigned: pa.assigned.map((a: { sphere: Sphere; edge: { edgeId: string; urgency: number } }) => ({
                  ...a,
                  sphere: assigneeSphere,
                })),
              }
            : pa
        );

        setFieldValue('assignedPerspectives', updatedAssignments);
      } else {
        const thePerspective = perspectives.find(
          (p) => p.system_name === perspectiveSystemName
        );
        if (!thePerspective) {
          return;
        }

        // Generate a unique temporary ID for new edges
        const tempEdgeId = `temp-edge-${crypto.randomUUID()}`;

        const newAssignment: PerspectiveAssignment = {
          mainSphereId: '',
          perspectiveProperties: {
            id: '',
            system_name: perspectiveSystemName,
            name: thePerspective.name,
          },
          assigned: [
            {
              sphere: assigneeSphere,
              edge: { edgeId: tempEdgeId, urgency: 3 },
            },
          ],
        };

        const newAssignments = [
          ...currentAssignments,
          newAssignment,
        ];
        setFieldValue('assignedPerspectives', newAssignments);
      }
    } else {
      const updatedAssignments = currentAssignments.filter(
        (pa) => pa.perspectiveProperties.system_name !== perspectiveSystemName
      );
      setFieldValue('assignedPerspectives', updatedAssignments);
    }

    setFieldValue(`properties.${perspectiveSystemName}`, assignee ?? '', true);
  };

  const handleAssigneesSelected = (
    assignees: string[] | null,
    _sphereId: string,
    perspectiveSystemName: string,
    assigneeSpheres?: Sphere[]
  ) => {
    const thePerspective = perspectives.find(
      (p) => p.system_name === perspectiveSystemName
    );
    if (!thePerspective) {
      return;
    }

    // Use current Formik values instead of stale prop to find existing assignments
    const currentAssignments = values.assignedPerspectives || [];

    if (assigneeSpheres && assigneeSpheres.length > 0) {
      const existingAssignment = currentAssignments.find(
        (pa) => pa.perspectiveProperties.system_name === perspectiveSystemName
      );

      // Check if the assignees are actually different
      if (existingAssignment) {
        const currentIds = new Set<string>(existingAssignment.assigned.map((a: any) => a.sphere.id as string));
        const newIds = new Set<string>(assigneeSpheres.map(s => s.id));

        const isSame = currentIds.size === newIds.size &&
                       [...currentIds].every(id => newIds.has(id));

        if (isSame) {
          return;
        }
      }

      // Preserve existing edge data for already-assigned spheres, generate unique IDs for new ones
      const newAssigned = assigneeSpheres.map(sphere => {
        const existingAssigned = existingAssignment?.assigned.find(
          (a: { sphere: Sphere; edge: { edgeId: string; urgency: number } }) => a.sphere.id === sphere.id
        );

        if (existingAssigned?.edge) {
          // Preserve existing edge data
          return {
            sphere,
            edge: existingAssigned.edge,
          };
        } else {
          // Generate unique temporary ID for new edges
          const tempEdgeId = `temp-edge-${crypto.randomUUID()}`;
          return {
            sphere,
            edge: { edgeId: tempEdgeId, urgency: 3 },
          };
        }
      });

      if (existingAssignment) {
        const updatedAssignments = currentAssignments.map((pa) =>
          pa.perspectiveProperties.system_name === perspectiveSystemName
            ? { ...pa, assigned: newAssigned }
            : pa
        );
        setFieldValue('assignedPerspectives', updatedAssignments);
      } else {
        const newAssignment: PerspectiveAssignment = {
          mainSphereId: '',
          perspectiveProperties: {
            id: '',
            system_name: perspectiveSystemName,
            name: thePerspective.name,
          },
          assigned: newAssigned,
        };
        const newAssignments = [...currentAssignments, newAssignment];
        setFieldValue('assignedPerspectives', newAssignments);
      }
    } else {
      const updatedAssignments = currentAssignments.filter(
        (pa: PerspectiveAssignment) => pa.perspectiveProperties.system_name !== perspectiveSystemName
      );
      setFieldValue('assignedPerspectives', updatedAssignments);
    }

    setFieldValue(`properties.${perspectiveSystemName}`, assignees?.join(',') ?? '', true);
  };

  function getValidationStyle(errors: any, persp: SchemaPerspective) {
    const errorMessage = errors.properties?.[persp.system_name];

    // strip off the namespace
    const innerKey = persp.label_key.replace(/^schema\./, '');

    // t() will now look up "center_baseschema.perspective.center_center.label"
    const perspectiveLabel = t(innerKey, {
      defaultValue: persp.label,
    });

    return {
      selectorClass: `perspective-selector ${errorMessage ? 'is-invalid' : ''}`,
      label: (
        <>
          {perspectiveLabel}
          {persp.required && <span style={{ color: 'red' }}> *</span>}
        </>
      ),
    };
  }

  return (
    <div className="perspective-assignments-panel">
      {isEditing ? (
        <>
          <h3>Perspective Assignments</h3>

          {/* Required Perspectives */}
          <div className="perspective-section">
            {[
              ...predefinedRequiredPerspectives,
              ...additionalRequiredPerspectives,
            ].map((persp) => {
              const { selectorClass, label } = getValidationStyle(
                errors,
                persp
              );
              return (
                <div className="perspective-item" key={persp.system_name}>
                  <PerspectiveSelector
                    className={selectorClass}
                    perspectiveSystemName={persp.system_name}
                    sphere={sphereWithCurrentAssignments}
                    organizationId={sphere.properties.sphereId}
                    onAssigneeSelected={handleAssigneeSelected}
                    onAssigneesSelected={handleAssigneesSelected}
                    showLabel={true}
                    label={label}
                    isEditing={isEditing}
                    stayOpen={persp.cardinality !== 'multiple'}
                    multi={persp.cardinality === 'multiple'}
                    lazy={true}
                  />
                  <ErrorMessage
                    name={`properties.${persp.system_name}`}
                    component="div"
                    className="invalid-feedback"
                  />
                </div>
              );
            })}
          </div>

          {/* Optional Perspectives with Show More functionality */}
          {/* Optional Perspectives with Show More functionality */}
          {optionalPerspectives.length > 0 && (
            <div className="perspective-section">
              <h4>Other Perspectives</h4>
              {showMore ? (
                optionalPerspectives.map((persp) => {
                  const { selectorClass, label } = getValidationStyle(
                    errors,
                    persp
                  );
                  return (
                    <div className="perspective-item" key={persp.system_name}>
                      <PerspectiveSelector
                        className={selectorClass}
                        perspectiveSystemName={persp.system_name}
                        sphere={sphereWithCurrentAssignments}
                        organizationId={sphere.properties.sphereId}
                        onAssigneeSelected={handleAssigneeSelected}
                        onAssigneesSelected={handleAssigneesSelected}
                        showLabel={true}
                        label={label}
                        isEditing={isEditing}
                        stayOpen={persp.cardinality !== 'multiple'}
                        multi={persp.cardinality === 'multiple'}
                        lazy={true}
                      />
                      <ErrorMessage
                        name={`properties.${persp.system_name}`}
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  );
                })
              ) : (
                <button
                  type="button"
                  className="show-more-button"
                  onClick={() => setShowMore(true)}
                >
                  Show More
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        // View Mode: Show only assigned perspectives
        assignedPerspectives.length > 0 && (
          <div className="perspective-section">
            {assignedPerspectives.map((pa) => {
              const persp = perspectives.find(
                (p) => p.system_name === pa.perspectiveProperties.system_name
              );
              return (
                <div
                  className="perspective-item"
                  key={pa.perspectiveProperties.system_name}
                >
                  <PerspectiveSelector
                    className="perspective-selector"
                    perspectiveSystemName={pa.perspectiveProperties.system_name}
                    sphere={sphereWithCurrentAssignments}
                    organizationId={sphere.properties.sphereId}
                    showLabel={true}
                    label={pa.perspectiveProperties.name}
                    isEditing={false}
                    stayOpen={false}
                    multi={persp?.cardinality === 'multiple'}
                  />
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default PerspectiveAssignmentsPanel;
